import React, { useState } from "react";
import { exampleTrajectories } from "./exampleTrajectoryData";
import {
  Trajectory,
  TrajectoryData,
} from "../../../components/blog/Trajectory";

// Define the nested structure type
type ExampleTrajectoriesData = Record<string, Record<string, TrajectoryData>>;

// Cast the imported data to the correct type
const typedExampleTrajectories = exampleTrajectories as ExampleTrajectoriesData;

// Create a mapping from scenario ID to display text
const scenarioIdToDisplayText: Record<string, string> = {};
for (const scenarioId in typedExampleTrajectories) {
  const models = typedExampleTrajectories[scenarioId];
  const firstModelId = Object.keys(models)[0];
  if (firstModelId && models[firstModelId]?.messages?.[1]?.content) {
    scenarioIdToDisplayText[scenarioId] =
      models[firstModelId].messages[1].content;
  } else {
    // Fallback to scenarioId if the structure is unexpected
    scenarioIdToDisplayText[scenarioId] = scenarioId;
  }
}

export const ExampleTrajectories = () => {
  // Use the keys from the new map for scenarioIds
  const scenarioIds = Object.keys(scenarioIdToDisplayText);
  const [selectedScenarioId, setSelectedScenarioId] = useState(scenarioIds[0]);

  const models = typedExampleTrajectories[selectedScenarioId] || {};
  const modelIds = Object.keys(models);
  const [selectedModelId, setSelectedModelId] = useState(modelIds[0] || "");

  // Update selected model when scenario changes
  React.useEffect(() => {
    const newModels = typedExampleTrajectories[selectedScenarioId] || {};
    const newModelIds = Object.keys(newModels);
    setSelectedModelId(newModelIds[0] || "");
  }, [selectedScenarioId]);

  const selectedTrajectory =
    typedExampleTrajectories[selectedScenarioId]?.[selectedModelId];

  return (
    <div className="p-4 border rounded-lg shadow-md bg-white dark:bg-gray-800 dark:border-gray-700">
      {/* Scenario Dropdown */}
      <div className="mb-4">
        <label
          htmlFor="scenario-select"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Dataset Question:{" "}
        </label>
        <select
          id="scenario-select"
          value={selectedScenarioId}
          onChange={(e) => setSelectedScenarioId(e.target.value)}
          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm rounded-md border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          {scenarioIds.map((id) => (
            <option key={id} value={id}>
              {/* Use the display text from the map here */}
              {scenarioIdToDisplayText[id]}
            </option>
          ))}
        </select>
      </div>

      {/* Model Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
        <nav className="-mb-px flex space-x-4" aria-label="Tabs">
          {/* Ensure modelIds are correctly fetched based on selectedScenarioId */}
          {Object.keys(typedExampleTrajectories[selectedScenarioId] || {}).map(
            (id) => (
              <button
                key={id}
                onClick={() => setSelectedModelId(id)}
                className={`whitespace-nowrap py-2 px-4 border-b-2 font-medium text-sm cursor-pointer focus:outline-none ${
                  selectedModelId === id
                    ? "border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                {id}
              </button>
            )
          )}
        </nav>
      </div>

      {/* Trajectory Display */}
      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-md overflow-x-auto">
        {selectedTrajectory ? (
          <Trajectory trajectory={selectedTrajectory} />
        ) : (
          <p className="text-gray-500 dark:text-gray-400">
            No trajectory data available for this selection.
          </p>
        )}
      </div>
    </div>
  );
};
